import { Organisation } from '@futrli-sdk/organisations';
import { APIClients, REQUEST_METHODS } from '@futrli/shared-services';
import { API_PATHS, getAxiosResponseData, interpolatePathParams, ROUTE_PARAMS } from '@futrli/shared-services';
import { makeRequestWithResponse } from './shared';
import {
  ExcelOrganisationValidationFailureResponse,
  ExcelUploadResponse,
  ExcelUploadScanStatusResponse,
  ExcelUploadsListResponse
} from '@futrli-sdk/predict';
import { QueryParams } from '../types';

/**
 * Fetches a list of organisations for a workspace.
 *
 * @param {string} workspaceId
 * @returns {Promise<Organisation[]>}
 */
export const fetchOrganisations = async (workspaceId: string): Promise<Organisation[]> => {
  const path = interpolatePathParams(API_PATHS.ORGANISATIONS, {
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId
  });
  const response = await APIClients.getAPIClient().makeRequest({
    payload: {
      request: {
        url: path
      }
    }
  });
  return getAxiosResponseData(response.data);
};

export const fetchOrganisationById = async (workspaceId: string, organisationId: string): Promise<Organisation> => {
  return getAxiosResponseData(
    await makeRequestWithResponse({
      url: interpolatePathParams(API_PATHS.ORGANISATION_BY_ID, {
        [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
        [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId
      })
    })
  );
};

export const deleteOrganisation = async (workspaceId: string, organisationId: string): Promise<void> => {
  return getAxiosResponseData(
    await makeRequestWithResponse({
      url: interpolatePathParams(API_PATHS.ORGANISATION_BY_ID, {
        [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
        [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId
      }),
      method: REQUEST_METHODS.DELETE
    })
  );
};

/**
 * Get S3 excel file upload link and id
 *
 * @param {string} workspaceId - related workspace
 * @param {string} name - name of the file
 * @param {string} type - what the excel file has data for, for example 'organisation'
 * @returns {Promise<ExcelUploadResponse>} - Returns
 * @throws error
 */
export const getExcelUploadPresignedUrl = async (
  workspaceId: string,
  file_name: string,
  file_type: string
): Promise<ExcelUploadResponse> => {
  const url = interpolatePathParams(API_PATHS.EXCEL_UPLOAD_GET_PRESIGNED_URL, {
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId
  });

  return makeRequestWithResponse<ExcelUploadResponse>({
    url,
    method: REQUEST_METHODS.POST,
    data: { file_name, file_type }
  });
};

/**
 * Get excel file virus scan status
 *
 * @param {string} workspaceId - related workspace
 * @param {string} excelId - related workspace
 * @returns {Promise<ExcelUploadScanStatusResponse>} - Returns
 * @throws error
 */
export const getFileScanStatus = async (
  workspaceId: string,
  excelId: string
): Promise<ExcelUploadScanStatusResponse> => {
  const url = interpolatePathParams(API_PATHS.EXCEL_UPLOAD_SCAN_STATUS, {
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.EXCEL_ID]: excelId
  });

  return makeRequestWithResponse<ExcelUploadScanStatusResponse>({
    url,
    method: REQUEST_METHODS.GET
  });
};

/**
 * Get excel file validation status
 *
 * @param {string} workspaceId - related workspace
 * @param {string} excelId - related workspace
 * @returns {Promise<{ excel_id: string; validation_status: string; error_details: ExcelOrganisationValidationFailureResponse | null; }>} - Returns
 * @throws error
 */
export const getExcelFileValidation = async (
  workspaceId: string,
  excelId: string,
  data: {
    country_code?: string;
    organisation_id?: string;
  }
): Promise<
  | ExcelOrganisationValidationFailureResponse
  | {
      excel_id: string;
      validation_status: string;
    }
> => {
  const url = interpolatePathParams(API_PATHS.EXCEL_FILE_VALIDATION, {
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.EXCEL_ID]: excelId
  });
  const response = await APIClients.getAPIClient().makeRequest({
    validateStatus: () => true,
    payload: {
      request: {
        method: REQUEST_METHODS.POST,
        data,
        url
      }
    }
  });
  return (response.data as unknown) as
    | ExcelOrganisationValidationFailureResponse
    | {
        excel_id: string;
        validation_status: string;
      };
};

/**
 * Generate protobuf file from excel file to populate organisation data
 *
 * @param {string} workspaceId - related workspace
 * @param {string} excelId - related excelId
 * @param {string} organisationId - related organisationId
 * @param {string} datasourceId - related datasourceId
 * @returns {Promise<{ excel_id: string; validation_status: string; error_details: ExcelOrganisationValidationFailureResponse | null; }>} - Returns
 * @throws error
 */
export const generateOrganisation = async (
  workspaceId: string,
  excelId: string,
  organisationId: string,
  datasourceId: string
): Promise<{
  excel_id: string;
  organisation_name: string;
  message: string;
}> => {
  const url = interpolatePathParams(API_PATHS.EXCEL_UPLOAD_ORGANISATION, {
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.EXCEL_ID]: excelId
  });

  return makeRequestWithResponse<{
    excel_id: string;
    organisation_name: string;
    message: string;
  }>({
    url,
    method: REQUEST_METHODS.POST,
    data: {
      organisation_id: organisationId,
      datasource_id: datasourceId,
      excel_id: excelId
    }
  });
};

/**
 * Get excel files list
 *
 * @param {string} workspaceId - related workspace
 * @param {string} organisationId - related organisationId
 */
export const getExcelFilesList = async (
  workspace_id: string,
  organisation_id: string,
  queryParams: QueryParams = {}
): Promise<ExcelUploadsListResponse> => {
  const url = interpolatePathParams(API_PATHS.ORGANISATION_EXCEL_FILES, {
    [ROUTE_PARAMS.WORKSPACE_ID]: workspace_id,
    [ROUTE_PARAMS.ORGANISATION_ID]: organisation_id
  });

  return makeRequestWithResponse<ExcelUploadsListResponse>({
    url,
    method: REQUEST_METHODS.GET,
    params: queryParams
  });
};
